import { Box } from '@chakra-ui/layout';
import { CardBody, type CardProps } from '@chakra-ui/react';
import { type ComponentProps, type MouseEventHandler, useRef } from 'react';

import { useGetLoggedInUser } from '@revelio/auth';
import { getOtherColor, getPrimaryColor, NoData } from '@revelio/replots';

import { useGaChartPreviewMeta } from '../../../../global-ai-assistant/ga-chart-preview-meta-context';
import { GlobalAiChartPreviewDataReporter } from '../../../../global-ai-assistant/global-ai-assistant-chart-preview-data-reporter';
import {
  GLOBAL_AI_INLINE_CHART_METRIC_PLOT_HEIGHT,
  GLOBAL_AI_INLINE_CHART_PLOT_HEIGHT,
} from '../../../constants/panelWidths';
import { useFilterContext } from '../../../filters/contexts/FilterProvider';
import { usePrimaryFilterState } from '../../../filters/hooks/usePrimaryFilterState';
import {
  DataProvider,
  MultiMetricDataProvider,
} from '../../DataProvider/DataProvider';
import type { MultiMetricProviderResult } from '../../DataProvider/MultiMetricProvider';
import { SnowflakeDataProvider } from '../../DataProvider/SnowflakeDataProvider';
import {
  DataProviderQueryStatus,
  type DataProviderSet,
  DataType,
  type SingleDataPoint,
  type SingleDataSet,
} from '../../DataProvider/types';
import { getChartDataSourceAccessErrorMessage } from '../../DataProvider/utils/chart-data-source-access';
import { needsMultiMetricData } from '../../DataProvider/utils/handlebars-field-config';
import { useTabContentConfig } from '../../PageConfigProvider/PageConfigProvider';
import type { MetricValues } from '../../PageConfigProvider/types/metric';
import { isSingleEntityChart } from '../../PageState/utils/is-single-entity-chart';
import { ChartCardShell } from '../ChartCardShell';
import { ChartError } from '../ChartError';
import {
  ChartActionMenu,
  ChartHeader,
  ChartHeaderAction,
  ChartHeaderTitleInfo,
  ChartSwitchers,
} from '../ChartHeader';
import { ChartLoader } from '../ChartLoader';
import { isParameterSelectionEqualToDataPoint } from '../utils/check-datapoint-in-parameter';
import { getParameterAggregatedData } from '../utils/get-parameter-aggregated-data';
import { getPrimaryParameterSelections } from '../utils/get-primary-parameter-selections';
import { groupAndFilterData } from '../utils/group-and-filter-data';
import { transformComparisonData } from '../utils/transform-comparison-data';
import { ChartCard } from './Chart';
import { ChartSelector, MultiMetricBodyChartSelector } from './ChartSelector';

type PickedDataChartProps = Pick<
  DataChartProps,
  | 'item'
  | 'headerSize'
  | 'chartType'
  | 'chartTitle'
  | 'primaryFilterId'
  | 'secondaryDimension'
  | 'metric'
  | 'pageId'
  | 'onClick'
>;

type DataWrappedChartProps = PickedDataChartProps & {
  editMode?: boolean;
  onLoading?: (loading: boolean) => void;
};

const ChartNoDataState = () => (
  <Box
    width="100%"
    height="100%"
    display="flex"
    alignItems="center"
    justifyContent="center"
  >
    <NoData />
  </Box>
);

export const DataWrappedChart = ({
  item,
  chartType,
  headerSize,
  chartTitle,
  primaryFilterId,
  secondaryDimension,
  metric,
  editMode,
  pageId,
  onClick,
  onLoading,
}: DataWrappedChartProps) => {
  const gaChartPreviewMeta = useGaChartPreviewMeta();
  const isGlobalAiChartPreview = gaChartPreviewMeta !== null;
  const { loggedInUser } = useGetLoggedInUser();
  const { revelioPageId } = useTabContentConfig();
  const showPrimaryEntitySwitcher = isSingleEntityChart(item);
  const loaderContainedInChart =
    editMode || showPrimaryEntitySwitcher || isGlobalAiChartPreview;
  const accessErrorMessage =
    !revelioPageId || isGlobalAiChartPreview
      ? getChartDataSourceAccessErrorMessage({
          metric,
          chartType,
          secondaryDimension,
          userTabs: loggedInUser.tabs ?? [],
        })
      : undefined;

  if (accessErrorMessage) {
    return (
      <ChartCardShell onClick={onClick}>
        <ChartError message={accessErrorMessage} />
      </ChartCardShell>
    );
  }

  if (item.plot?.dataSource === 'snowflake' && item.plot?.snowflakeConfig) {
    return (
      <Box
        width="100%"
        height="100%"
        {...(loaderContainedInChart && { position: 'relative' })}
      >
        <SnowflakeDataProvider
          config={item.plot.snowflakeConfig}
          chartType={chartType}
          defaultColor={chartType === 'bar' ? item.plot.color : undefined}
        >
          {({ data, error, status }) => (
            <>
              {gaChartPreviewMeta ? (
                <GlobalAiChartPreviewDataReporter
                  itemId={item.id}
                  branch="snowflake"
                  status={status}
                  error={error}
                  data={data}
                  chartType={chartType}
                  metric={metric}
                />
              ) : null}
              {(() => {
                if (status === DataProviderQueryStatus.Error) {
                  return (
                    <ChartCardShell onClick={onClick}>
                      <ChartError />
                    </ChartCardShell>
                  );
                }

                onLoading?.(status === DataProviderQueryStatus.Fetching);

                if (status === DataProviderQueryStatus.Fetching) {
                  return (
                    <ChartCardShell onClick={onClick}>
                      <ChartLoader onClick={onClick} />
                    </ChartCardShell>
                  );
                }

                if (!data) {
                  return (
                    <ChartCardShell onClick={onClick}>
                      <ChartNoDataState />
                    </ChartCardShell>
                  );
                }

                return (
                  <DataChart
                    item={item}
                    chartType={chartType}
                    headerSize={headerSize}
                    chartTitle={chartTitle}
                    primaryFilterId={primaryFilterId}
                    secondaryDimension={secondaryDimension}
                    metric={metric}
                    data={data}
                    dataType={data.type}
                    pageId={pageId}
                    onClick={onClick}
                  />
                );
              })()}
            </>
          )}
        </SnowflakeDataProvider>
      </Box>
    );
  }

  if (needsMultiMetricData(item, chartType)) {
    return (
      <Box
        width="100%"
        height="100%"
        {...(loaderContainedInChart && { position: 'relative' })}
      >
        <MultiMetricDataProvider
          item={item}
          chartType={chartType}
          primaryDimension={primaryFilterId}
          onClick={onClick}
        >
          {({ data, error, status }) => (
            <>
              {gaChartPreviewMeta ? (
                <GlobalAiChartPreviewDataReporter
                  itemId={item.id}
                  branch="multi"
                  status={status}
                  error={error}
                  data={data}
                  chartType={chartType}
                  metric={metric}
                />
              ) : null}
              {(() => {
                if (status === DataProviderQueryStatus.Error) {
                  return (
                    <ChartCardShell onClick={onClick}>
                      <ChartError />
                    </ChartCardShell>
                  );
                }

                onLoading?.(status === DataProviderQueryStatus.Fetching);

                if (status === DataProviderQueryStatus.Fetching) {
                  return (
                    <ChartCardShell onClick={onClick}>
                      <ChartLoader onClick={onClick} />
                    </ChartCardShell>
                  );
                }

                if (!data) {
                  return (
                    <ChartCardShell onClick={onClick}>
                      <ChartNoDataState />
                    </ChartCardShell>
                  );
                }

                return (
                  <MultiMetricDataChart
                    item={item}
                    chartType={chartType}
                    headerSize={headerSize}
                    chartTitle={chartTitle}
                    primaryFilterId={primaryFilterId}
                    metric={metric}
                    data={data}
                    onClick={onClick}
                  />
                );
              })()}
            </>
          )}
        </MultiMetricDataProvider>
      </Box>
    );
  }

  return (
    <Box
      width="100%"
      height="100%"
      {...(loaderContainedInChart && { position: 'relative' })}
    >
      <DataProvider
        chartType={chartType}
        primaryDimension={primaryFilterId}
        secondaryDimension={secondaryDimension}
        metric={metric}
      >
        {({ data, error, status }) => (
          <>
            {gaChartPreviewMeta ? (
              <GlobalAiChartPreviewDataReporter
                itemId={item.id}
                branch="main"
                status={status}
                error={error}
                data={data}
                chartType={chartType}
                metric={metric}
              />
            ) : null}
            {(() => {
              if (status === DataProviderQueryStatus.Error) {
                return (
                  <ChartCardShell onClick={onClick}>
                    <ChartError />
                  </ChartCardShell>
                );
              }

              onLoading?.(status === DataProviderQueryStatus.Fetching);

              if (status === DataProviderQueryStatus.Fetching) {
                return (
                  <ChartCardShell onClick={onClick}>
                    <ChartLoader onClick={onClick} />
                  </ChartCardShell>
                );
              }

              if (!data) {
                return (
                  <ChartCardShell onClick={onClick}>
                    <ChartNoDataState />
                  </ChartCardShell>
                );
              }

              return (
                <DataChart
                  item={item}
                  chartType={chartType}
                  headerSize={headerSize}
                  chartTitle={chartTitle}
                  primaryFilterId={primaryFilterId}
                  secondaryDimension={secondaryDimension}
                  metric={metric}
                  data={data}
                  dataType={data.type}
                  pageId={pageId}
                  onClick={onClick}
                />
              );
            })()}
          </>
        )}
      </DataProvider>
    </Box>
  );
};

type ChartActionMenuProps = Pick<
  ComponentProps<typeof ChartActionMenu>,
  'primaryFilterId' | 'dataType'
>;

type BodyChartSelectorProps = Pick<
  ComponentProps<typeof ChartSelector>,
  | 'chartType'
  | 'item'
  | 'metric'
  | 'secondaryDimension'
  | 'pageId'
  | 'primaryParameterValues'
>;

type DataChartProps = ChartActionMenuProps &
  BodyChartSelectorProps & {
    data: DataProviderSet;
    dataType: DataType;
    headerSize?: 'sm' | 'md';
    hideActionMenu?: boolean;
    chartTitle: string;
    onClick?: MouseEventHandler<HTMLDivElement>;
  } & CardProps;
const DataChart = ({
  item,
  chartType,
  headerSize,
  hideActionMenu = false,
  chartTitle,
  primaryFilterId,
  secondaryDimension,
  metric,
  data,
  dataType,
  pageId,
  onClick,
  ...cardProps
}: DataChartProps) => {
  const printRef = useRef<HTMLDivElement>(null);
  const gaChartPreviewMeta = useGaChartPreviewMeta();
  const isGlobalAiChartPreview = gaChartPreviewMeta !== null;
  const showActionMenu = !hideActionMenu && !isGlobalAiChartPreview;
  const showPrimaryEntitySwitcher = isSingleEntityChart(item);
  const { activePrimaryFilterLength } = usePrimaryFilterState(primaryFilterId);

  const { config: tabContentConfig } = useTabContentConfig();
  const { activeFilterMap } = useFilterContext();

  const reportParameters = tabContentConfig?.reportParameters ?? [];

  const aggregatedData = getParameterAggregatedData({
    data,
    plotParameters: item?.plot?.plotParameters ?? [],
    reportParameters,
    activeFilterMap,
  });

  const comparisonTransformation = item?.plot?.comparisonTransformation;
  const shouldApplyComparisonTransformation =
    chartType !== 'diverging-bar-combo' && !!comparisonTransformation;

  let transformedData = aggregatedData;
  if (shouldApplyComparisonTransformation && comparisonTransformation) {
    transformedData = transformComparisonData(
      aggregatedData,
      comparisonTransformation
    );
  }

  const { groupBySegment, plotFilters, hideOther } = item?.plot ?? {};
  const filteredData = groupAndFilterData({
    data: transformedData,
    groupBySegment,
    plotFilters,
    hideOther,
  });
  const gaCompactMetricPreview =
    isGlobalAiChartPreview &&
    activePrimaryFilterLength === 1 &&
    chartType === 'bar' &&
    filteredData?.type === DataType.SINGLE &&
    filteredData.data.length === 1;
  const gaInlinePlotHeight = gaCompactMetricPreview
    ? GLOBAL_AI_INLINE_CHART_METRIC_PLOT_HEIGHT
    : GLOBAL_AI_INLINE_CHART_PLOT_HEIGHT;

  const isCompetitorBarChart =
    chartType === 'bar' && item?.plot?.dimensionSource === 'competitors';
  const primaryParameterValues = getPrimaryParameterSelections({
    reportParameters,
    activeFilterMap,
    primaryFilterId: isCompetitorBarChart
      ? tabContentConfig?.primaryFilter?.id
      : undefined,
  });
  const chartSelectorData =
    chartType === 'bar' &&
    filteredData?.type === DataType.SINGLE &&
    primaryParameterValues.length > 0
      ? ({
          ...filteredData,
          data: filteredData.data.map((point) => ({
            ...point,
            color:
              point.color ??
              (primaryParameterValues.some((p) =>
                isParameterSelectionEqualToDataPoint(
                  p,
                  point as SingleDataPoint
                )
              )
                ? getPrimaryColor()
                : getOtherColor()),
          })),
        } as SingleDataSet)
      : filteredData;

  const ChartRender = (
    <ChartSelector
      chartType={chartType}
      data={chartSelectorData}
      item={item}
      metric={metric}
      secondaryDimension={secondaryDimension}
      pageId={pageId}
      primaryParameterValues={primaryParameterValues}
    />
  );

  const titleCentered = item?.isTitleCentered === true;

  const chartBodyContent = isGlobalAiChartPreview ? (
    <Box
      w="100%"
      flexShrink={0}
      minH={gaInlinePlotHeight}
      h={gaInlinePlotHeight}
      {...(gaCompactMetricPreview
        ? {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transform: 'translateY(-18px)',
          }
        : {})}
    >
      {ChartRender}
    </Box>
  ) : (
    ChartRender
  );

  return (
    <ChartCard onClick={onClick} {...cardProps}>
      <ChartHeader
        headerSize={headerSize}
        {...(showActionMenu && { paddingRight: '0.5rem' })}
        {...(titleCentered && { justifyContent: 'center' })}
      >
        <ChartHeaderTitleInfo info={item?.info} title={chartTitle} />
        <Box
          maxWidth="60%"
          {...(titleCentered && { position: 'absolute', right: '0.5rem' })}
        >
          <ChartHeaderAction>
            <ChartSwitchers
              primaryFilterId={primaryFilterId}
              isSingleEntityChart={showPrimaryEntitySwitcher}
              {...(showActionMenu && { width: 'calc(100% - 20px)' })}
            />
            {showActionMenu && (
              <ChartActionMenu
                printRef={printRef}
                item={item}
                metric={metric}
                title={chartTitle}
                primaryFilterId={primaryFilterId}
                slim={headerSize === 'sm'}
                data={transformedData}
                dataType={
                  shouldApplyComparisonTransformation
                    ? transformedData.type
                    : dataType
                }
                modalContent={ChartRender}
              />
            )}
          </ChartHeaderAction>
        </Box>
      </ChartHeader>
      <CardBody
        ref={printRef}
        data-testid="card-body"
        padding="0.5rem 14px 0.5rem"
        {...(isGlobalAiChartPreview
          ? {
              height: 'auto',
              minH: 'min-content',
              overflow: 'visible',
              flexShrink: 0,
              position: 'relative',
            }
          : {
              height: 'calc(100% - 40px)',
            })}
      >
        {chartBodyContent}
      </CardBody>
    </ChartCard>
  );
};

type MultiMetricDataChartProps = Pick<
  DataChartProps,
  | 'item'
  | 'chartType'
  | 'primaryFilterId'
  | 'chartTitle'
  | 'headerSize'
  | 'hideActionMenu'
  | 'onClick'
> & {
  data: MultiMetricProviderResult;
  secondaryDimension?: DataChartProps['secondaryDimension'];
  metric?: DataChartProps['metric'];
} & CardProps;
const MultiMetricDataChart = ({
  item,
  chartType,
  primaryFilterId,
  chartTitle,
  data,
  headerSize,
  hideActionMenu,
  onClick,
  metric,
  ...cardProps
}: MultiMetricDataChartProps) => {
  const printRef = useRef<HTMLDivElement>(null);
  const gaChartPreviewMeta = useGaChartPreviewMeta();
  const isGlobalAiChartPreview = gaChartPreviewMeta !== null;
  const showActionMenu = !hideActionMenu && !isGlobalAiChartPreview;
  const showPrimaryEntitySwitcher = isSingleEntityChart(item);

  const exportSeriesForMenu = (() => {
    const defaultMetric = item.plot?.defaultMetricOptions?.[0];
    if (defaultMetric !== undefined && data.byMetric[defaultMetric]) {
      return data.byMetric[defaultMetric];
    }
    const firstEntry = (
      Object.entries(data.byMetric) as [
        MetricValues,
        DataProviderSet | undefined,
      ][]
    ).find(([, series]) => series !== undefined);
    if (firstEntry) {
      return firstEntry[1];
    }
    return data.fallback;
  })();

  const ChartRender = (
    <MultiMetricBodyChartSelector
      item={item}
      chartType={chartType}
      data={data}
      metric={metric}
    />
  );

  const chartBodyContent = isGlobalAiChartPreview ? (
    <Box
      w="100%"
      flexShrink={0}
      minH={GLOBAL_AI_INLINE_CHART_PLOT_HEIGHT}
      h={GLOBAL_AI_INLINE_CHART_PLOT_HEIGHT}
    >
      {ChartRender}
    </Box>
  ) : (
    ChartRender
  );

  return (
    <ChartCard onClick={onClick} {...cardProps}>
      <ChartHeader
        headerSize={headerSize}
        {...(showActionMenu && { paddingRight: '0.5rem' })}
      >
        <ChartHeaderTitleInfo info={item?.info} title={chartTitle} />
        <Box maxWidth="60%">
          <ChartHeaderAction>
            <ChartSwitchers
              primaryFilterId={primaryFilterId}
              isSingleEntityChart={showPrimaryEntitySwitcher}
              {...(showActionMenu && { width: 'calc(100% - 20px)' })}
            />
            {showActionMenu && (
              <ChartActionMenu
                printRef={printRef}
                item={item}
                title={chartTitle}
                primaryFilterId={primaryFilterId}
                slim={headerSize === 'sm'}
                data={exportSeriesForMenu ?? data}
                dataType={exportSeriesForMenu?.type}
                modalContent={ChartRender}
              />
            )}
          </ChartHeaderAction>
        </Box>
      </ChartHeader>
      <CardBody
        ref={printRef}
        data-testid="card-body"
        padding="0.5rem 14px 0.5rem"
        {...(isGlobalAiChartPreview
          ? {
              height: 'auto',
              minH: 'min-content',
              overflow: 'visible',
              flexShrink: 0,
              position: 'relative',
            }
          : {
              height: 'calc(100% - 40px)',
            })}
      >
        {chartBodyContent}
      </CardBody>
    </ChartCard>
  );
};
